import { Empty } from '@frontend/app/refresh-components';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { BoxCheckIcon } from '@revfluence/fresh-icons/solid/esm';
import React, { useEffect, useState } from 'react';
import { CatalogOrderRequestInput } from '@frontend/app/types/globalTypes';

interface ProductPagePreviewProps {
  catalogPreviewIframeUrl: string;
  isCatalogPreviewIframeUrlLoading: boolean;
  isPreview?: boolean;
  onSubmit?: (catalogOrderRequest: CatalogOrderRequestInput) => void;
  backgroundColor?: string;
  onBack: () => void;
}

const ALLOWED_ORIGINS = ['https://creators.aspireiq.com', 'https://creators.aspireiq-dev.com', 'http://localhost:3000'];

export const ProductPagePreview = ({ catalogPreviewIframeUrl, isCatalogPreviewIframeUrlLoading, isPreview, onSubmit, backgroundColor, onBack }: ProductPagePreviewProps) => {
  const [isIframeLoading, setIsIframeLoading] = useState(true);

  useEffect(() => {
    setIsIframeLoading(true);
  }, [catalogPreviewIframeUrl]);

  useEffect(() => {
    const callback = (event: MessageEvent) => {
      if (
        ALLOWED_ORIGINS.includes(event.origin) &&
        event.data?.type === 'order-request-created'
      ) {
        const payload: CatalogOrderRequestInput = event.data.payload;
        if (payload.creatorOrderRequest) {
          onSubmit?.({
            ...payload,
            creatorOrderRequest: {
              ...payload.creatorOrderRequest,
              line_items: payload.creatorOrderRequest.line_items.map((lineItem) => ({
                ...lineItem,
                variant_id: String(lineItem.variant_id),
              })),
            },
          });
        }
      } else if (
        ALLOWED_ORIGINS.includes(event.origin) &&
        event.data?.type === 'back'
      ) {
        onBack();
      }
    };

    window.addEventListener('message', callback);

    return () => {
      window.removeEventListener('message', callback);
    };
  }, [onSubmit, onBack]);

  useEffect(() => {
    const callback = (event: MessageEvent) => {
      if (
        ALLOWED_ORIGINS.includes(event.origin) &&
        event.data?.type === 'order-request-page-loaded'
      ) {
        setIsIframeLoading(false);
      }
    };

    window.addEventListener('message', callback);

    return () => {
      window.removeEventListener('message', callback);
    };
  }, []);

  if (isPreview && !catalogPreviewIframeUrl) {
    return (
      <Empty
        title="No catalog selected"
        description="Please select a catalog to preview the product page"
        icon={BoxCheckIcon}
      />
    );
  }

  if (isCatalogPreviewIframeUrlLoading) {
    return (
      <div className="w-full h-full flex flex-col items-center gap-4 p-6 @md:p-16" style={{ background: backgroundColor }}>
        <div className="h-full w-full max-w-screen-lg rounded-4xl bg-white overflow-hidden">
          <Skeleton className="w-full h-10" />
          <Skeleton className="w-full h-10" />
          <Skeleton className="w-full h-10" />
          <Skeleton className="w-full h-10" />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col items-center gap-4 @md:p-16" style={{ background: backgroundColor }}>
      <div className="h-full w-full max-w-screen-lg rounded-4xl bg-white overflow-hidden">
        {isIframeLoading && (
          <div className="w-full flex flex-col gap-4 p-6">
            <Skeleton className="w-full h-10" />
            <Skeleton className="w-full h-10" />
            <Skeleton className="w-full h-10" />
            <Skeleton className="w-full h-10" />
          </div>
        )}
        <iframe
          src={catalogPreviewIframeUrl}
          className="w-full h-full"
        />
      </div>
    </div>
  );
};
